// @flow
import React from 'react';
import { type ContextRouter, withRouter } from 'react-router-dom';
import cx from 'classnames';
import qs from 'query-string';
import { isBrowser } from 'react-device-detect';
import styled from 'styled-components';
import { FaHeart, FaCopy } from 'react-icons/fa';
import { MdShoppingCart, MdChat, MdRefresh, MdAutorenew, MdTimer } from 'react-icons/md';
import { AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import { useSortBy, useTable, useTableState } from 'react-table';
import { numberWithCommas } from 'lib/common';
import './ProductPrice.scss';
import Button from '../../common/Button';
import {
  BaseActions,
  CartActions,
  WishActions,
  SettingsActions,
} from '../../../store/actionCreators';

const Styles = styled.div`
  /* This is required to make the table full-width */
  display: block;
  max-width: 100%;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* This will make the table scrollable when it gets too small */

  .tableWrap {
    display: block;
    max-width: 100%;
    border-bottom: 1px solid black;
  }

  .price {
    margin-top: 4px;
    float: left;
    font-size: 0.8rem;
  }

  .price_vat {
    margin-top: 4px;
    float: left;
    font-size: 1.2rem;
    font-weight: bold;
  }

  .status {
    &.SALE {
      color: #212529;
    }

    &.IN_STOCK {
      font-weight: bold;
      color: #1c7ed6;
    }

    &.SHIP_TODAY {
      font-weight: bold;
      color: #f03e3e;
    }

    &.STOP {
      color: #f03e3e;
    }
  }
  
  .status-td {
    width: 80px;
  }

  .unit-td {
    width: 70px;
  }

  .cart {
    float: right;
    margin-right: 8px;
    margin-bottom: 8px;
  }
    
  .original-price-td {
    width: 110px;
  }

  .price-td {
    width: 110px;
  }

  .price-vat-td {
    width: 110px;
  }
    
  .id-td {
    width: 150px;
  }
  
  .part-number-td {
    width: 120px;
  }
  
  .cas-number-td {
    width: 120px;
  }
  
  .description-td {
    min-width: 500px;
  }

  .status-td {
    width: 80px;
  }
  
  .quantity-td {
    width: 60px;
  }
  
  .quantity-cart-wish-td {
    width: 150px;
  }

  .mark {
    background-color: #ff9;
  }
  
  .part-number-td {
    cursor: pointer;
    position: relative;
    
    &:hover {
      background-color: #e9ecef;
      
      .copy-icon {
        opacity: 1;
      }
    }
    
    .copy-icon {
      position: absolute;
      right: 5px;
      top: 50%;
      transform: translateY(-50%);
      opacity: 0;
      transition: opacity 0.2s;
      font-size: 12px;
      color: #495057;
    }
    
    &.copied {
      background-color: #d3f9d8;
      transition: background-color 0.3s;
    }
  }  

  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    font-size: 100%;
    font: inherit;

    th {
      font-weight: bold;
      border: 1px solid #ccc;
      padding: 4px 5px;
      white-space: nowrap;
    }

    td {
      border: 1px solid #ccc;
      padding: 3px 8px;
      text-align: center;
      white-space: normal;
      max-width: 500px;
      font-size: 14px;
      overflow: hidden;
    }
    
    td.description-td {
      white-space: normal;
      text-align: left;
      word-break: break-word;
    }

    div {
      padding: 2px 0;
      font-size: 0.8rem;
      white-space: normal;
    }

    tr {
      border-top: 1px solid #ccc;
      background-color: #fff;
    }

    thead {
      tr:nth-child(1) {
        background-color: #1971c2;
        color: white;
        height: 2rem;
        font-weight: bold;
      }    
    }

    tr:nth-child(2n) {
      background-color: #f8f8f8;
    }

    tbody {
      tr:hover {
        background: #d0ebff;
      }
    }
  }

  .pagination {
    padding: 0.5rem;
  }
`;

const defaultColumn = {
  sort: 'numeric',
};

// Let's add a fetchData method to our Table component that will be used to fetch
// new data when pagination state changes
// We can also add a loading state to let our table know it's loading new data
function Table({
  columns,
  data,
  productId,
  user,
  logged,
  currentUsername,
  onLogin,
  fetchData,
  loading,

  serverData,
  setData,
  totalCount,
  history,
  quantity,
  setQuantity,
  onChannel,
}) {
  // Use useTableState to hoist the state (and state updater) out of the table and control it
  const tableState = useTableState({});

  // Fetch data on component mount
  React.useEffect(() => {
    fetchData({});
  }, [fetchData]);

  const addCart = async (optionId, productPriceId, index) => {
    try {
      const indexQuantity = quantity[index];
      if (logged) {
        await CartActions.addItemCart({
          productId,
          productPriceId,
          quantity: indexQuantity,
        });
        await CartActions.getCart();
        BaseActions.showToast({
          type: 'success',
          message: '장바구니에 담았습니다.',
        });
      } else {
        CartActions.addItemAsNonRegisteredCart({
          product: productId,
          variant: optionId,
          productId,
          productPriceId,
          quantity: indexQuantity,
          shippingMethod: 'B7J655BTV7XK',
        });
        await CartActions.getAsNonRegisteredCart({ items: window.CacheBy.LocalCart.items });
        BaseActions.showToast({
          type: 'success',
          message: '장바구니에 담았습니다.',
        });
      }
    } catch (e) {
      console.log(e);
      BaseActions.showToast({
        type: 'error',
        message: '장바구니 담기가 실패하였습니다.',
      });
    }
  };

  const addWish = async (optionId, productPriceId, index) => {
    try {
      if (logged) {
        const indexQuantity = quantity[index];
        await WishActions.addItemWish({
          productId,
          productPriceId,
          quantity: indexQuantity,
        });
        await WishActions.getWish();
        BaseActions.showToast({
          type: 'success',
          message: '상품을 찜 하였습니다.',
        });
      } else {
        history.push('/login');
      }
    } catch (e) {
      console.log(e);
      BaseActions.showToast({
        type: 'error',
        message: '찜하기가 실패하였습니다.',
      });
    }
  };

  const onChange = index => (e: SyntheticInputEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newArr = [...quantity];
    newArr[index] = Number(value.replace(/\D/, ''));
    setQuantity(newArr);
  };
  
  // 수량 증가 함수
  const increaseQuantity = (index) => {
    let newArr = [...quantity];
    newArr[index] = quantity[index] + 1;
    setQuantity(newArr);
  };
  
  // 수량 감소 함수
  const decreaseQuantity = (index) => {
    if (quantity[index] > 1) {
      let newArr = [...quantity];
      newArr[index] = quantity[index] - 1;
      setQuantity(newArr);
    }
  };

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      debug: false,
      state: tableState, // Pass our hoisted table state
    },
    useSortBy,
  );

  const { part_number } = qs.parse(history.location.search);

  // // Render the UI for your table
  // if (!isBrowser) {
  //   return (
  //     <div className="tableWrap">
  //       <table {...getTableProps()}>
  //         <thead>
  //           <tr>
  //             <th>상품 옵션 정보</th>
  //           </tr>
  //         </thead>
  //         <tbody>
  //           {rows.map((row, i) => {
  //             return (
  //               prepareRow(row) || (
  //                 <tr {...row.getRowProps()} key={row.id || i}>
  //                   {row.cells.map(cell => {
  //                     if (cell.column.id === 'part_number') {
  //                       return (
  //                         <div key={cell.column.id} className={cx({ mark: part_number === cell.value })}>
  //                           <strong>카탈로그 번호 : </strong>
  //                           {cell.value}
  //                         </div>
  //                       );
  //                     }
  //                     if (cell.column.id === 'cas_number') {
  //                       return (
  //                         <div key={cell.column.id} {...cell.getCellProps()}>
  //                           <strong>CAS 번호 : </strong>
  //                           {cell.value || '-'}
  //                         </div>
  //                       );
  //                     }
  //                     if (cell.column.id === 'unit') {
  //                       return (
  //                         <div key={cell.column.id} {...cell.getCellProps()}>
  //                           <strong>단위 : </strong>
  //                           {cell.value}
  //                         </div>
  //                       );
  //                     }
  //                     if (cell.column.id === 'description') {
  //                       return <div key={cell.column.id} {...cell.getCellProps()}>{cell.value}</div>;
  //                     }
  //                     if (cell.column.id === 'price_vat') {
  //                       return <div key={cell.column.id} className="price_vat">{numberWithCommas(cell.value)}원</div>;
  //                     }
  //                     if (cell.column.id === 'id') {
  //                       return (
  //                         <div key={cell.column.id} className="cart">
  //                           <div className="quantity-control">
  //                             <button 
  //                               type="button" 
  //                               className="quantity-btn minus"
  //                               onClick={() => decreaseQuantity(i)}
  //                             >
  //                               -
  //                             </button>
  //                             <input
  //                               className="quantity"
  //                               onChange={onChange(i)}
  //                               name="quantity"
  //                               placeholder="수량"
  //                               value={quantity[i]}
  //                               maxLength="20"
  //                               required
  //                               type="text"
  //                               pattern="[1-9]*"
  //                             />

  //                             <button 
  //                               type="button" 
  //                               className="quantity-btn plus"
  //                               onClick={() => {
  //                                 if (quantity[i] < 1000) {
  //                                   increaseQuantity(i);
  //                                 }
  //                               }}
  //                             >
  //                               +
  //                             </button>
  //                           </div>
  //                           {cell.row.values.price === '0' ? (
  //                             <Button
  //                               theme="outline"
  //                               color="green"
  //                               onClick={onChannel}
  //                             >
  //                               <MdChat className="chat-icon" />
  //                             </Button>
  //                           ) : (
  //                             <Button
  //                               theme="outline"
  //                               color="blue"
  //                               onClick={() => addCart(cell.value, cell.row.original.id, i)}
  //                             >
  //                               <MdShoppingCart className="cart-icon" />
  //                             </Button>
  //                           )}
  //                           <Button
  //                             theme="outline"
  //                             color="red"
  //                             onClick={() => addWish(cell.value, cell.row.original.id, i)}
  //                           >
  //                             <FaHeart className="wish-icon" />
  //                           </Button>
  //                         </div>
  //                       );
  //                     }
  //                     return true;
  //                   })}
  //                 </tr>
  //               )
  //             );
  //           })}
  //         </tbody>
  //       </table>
  //     </div>
  //   );
  // }

  const status = {
    SALE: '재고문의',
    IN_STOCK: '재고보유',
    SHIP_TODAY: '오늘출고',
    STOP: '견적요청',
  };

  // 모바일 카드 렌더링 함수
  const renderMobileCard = (row, i) => {
    const rowData = row.original;
    const isHighlighted = part_number && rowData.part_number === part_number;
    return (
      <div 
        key={row.id || i} 
        className={cx('mobile-card', {
          'highlighted': isHighlighted
        })}
      >
        <div className="card-content">
          {/* 첫 번째 줄: 파트넘버 + 상태 */}
          <div className="mobile-card-row-1">
            {/* 파트넘버 */}
            <div className="part-info">
              <div 
                className="part-number"
                onClick={() => {
                  if (rowData.part_number) {
                    navigator.clipboard.writeText(rowData.part_number)
                      .then(() => {
                        BaseActions.showToast({
                          type: 'success',
                          message: `카탈로그 번호 "${rowData.part_number}"가 클립보드에 복사되었습니다.`,
                        });
                      })
                      .catch(err => {
                        console.error('클립보드 복사 실패:', err);
                        BaseActions.showToast({
                          type: 'error',
                          message: '클립보드 복사에 실패했습니다.',
                        });
                      });
                  }
                }}
                title="클릭하여 복사하기"
              >
                {rowData.part_number}
                <FaCopy className="copy-icon" />
              </div>
            </div>
            
            {/* 상태 */}
            <div className="status-section">
              <span className={cx('status-badge', rowData.status)}>
                {status[rowData.status]}
              </span>
            </div>
          </div>
          
          {/* 두 번째 줄: 상품 설명 */}
          <div className="mobile-card-row-2">
            <div className="description-section">
              <div className="description">
                {rowData.description}
              </div>
              <div className="detail-info">
                <span className="cas-info">CAS: {rowData.cas_number || '-'}</span>
                <span className="stock-info">재고: {rowData.quantity > 0 ? rowData.quantity : '-'}</span>
                <span className="unit-info">단위: {rowData.unit}</span>
              </div>
            </div>
          </div>
          
          {/* 세 번째 줄: 수량 | 가격 */}
          <div className="mobile-card-row-3">
            {/* 수량 조절 */}
            <div className="quantity-control">
              <button 
                type="button" 
                className="quantity-btn minus"
                onClick={() => decreaseQuantity(i)}
              >
                <AiOutlineMinus />
              </button>
              <input
                className="quantity"
                onChange={onChange(i)}
                name="quantity"
                placeholder="수량"
                value={quantity[i]}
                maxLength="20"
                required
                type="text"
                pattern="[1-9]*"
              />
              <button 
                type="button" 
                className="quantity-btn plus"
                onClick={() => {
                  if (quantity[i] < 1000) {
                    increaseQuantity(i);
                  }
                }}
              >
                <AiOutlinePlus />
              </button>
            </div>
            
            {/* 가격 정보 */}
            <div className="price-info">
              {/* 1줄: 기존 가격 (할인이 있을 때만 취소선) */}
              <div className="original-price-line">
                <span 
                  className={cx('original-price', {
                    'strikethrough': !!(rowData.original_price - rowData.price)
                  })}
                >
                  {numberWithCommas(rowData.original_price)}원
                </span>
              </div>
              
              {/* 2줄: 할인된 가격 (할인이 있을 때만 표시) */}
              {!!(rowData.original_price - rowData.price) && (
                <div className="discounted-price-line">
                  <span className="discount-label">할인가</span>
                  <span className="discounted-price">
                    {numberWithCommas(rowData.price)}원
                  </span>
                </div>
              )}
              
              {/* 3줄: VAT 포함 가격 */}
              <div className="vat-price-line">
                <span className="vat-label">(VAT포함)</span>
                <span className="vat-price">
                  {numberWithCommas(rowData.price_vat)}원
                </span>
              </div>
            </div>
          </div>
          
          {/* 네 번째 줄: 액션 버튼 */}
          <div className="mobile-card-row-4">
            <div className="action-buttons">
              {rowData.price === '0' ? (
                <button
                  className="action-btn inquiry-btn"
                  onClick={() => onChannel(rowData, '상품 옵션 정보 - (모바일)')}
                  title="문의하기"
                >
                  <MdChat />
                  <span>문의</span>
                </button>
              ) : (
                <React.Fragment>
                  <button
                    className="action-btn inquiry-btn"
                    onClick={() => onChannel(rowData, '상품 옵션 정보 - (모바일)')}
                    title="문의하기"
                  >
                    <MdChat />
                    <span>문의</span>
                  </button>
                  <button
                    className="action-btn cart-btn"
                    onClick={() => addCart(rowData.id, rowData.id, i)}
                    title="장바구니 담기"
                  >
                    <MdShoppingCart />
                    <span>장바구니</span>
                  </button>
                  <button
                    className="action-btn wish-btn"
                    onClick={() => addWish(rowData.id, rowData.id, i)}
                    title="찜하기"
                  >
                    <FaHeart />
                    <span>찜하기</span>
                  </button>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // PC용 카드 렌더링 함수
  const renderDesktopCard = (row, i) => {
    const rowData = row.original;
    const isHighlighted = part_number && rowData.part_number === part_number;
    return (
      <div 
        key={row.id || i} 
        className={cx('desktop-card', {
          'highlighted': isHighlighted
        })}
      >
        <div className="card-content">
          {/* 카탈로그 번호 */}
          <div className="part-info">
            <div 
              className="part-number"
              onClick={() => {
                if (rowData.part_number) {
                  navigator.clipboard.writeText(rowData.part_number)
                    .then(() => {
                      BaseActions.showToast({
                        type: 'success',
                        message: `카탈로그 번호 "${rowData.part_number}"가 클립보드에 복사되었습니다.`,
                      });
                    })
                    .catch(err => {
                      console.error('클립보드 복사 실패:', err);
                      BaseActions.showToast({
                        type: 'error',
                        message: '클립보드 복사에 실패했습니다.',
                      });
                    });
                }
              }}
              title="클릭하여 복사하기"
            >
              {rowData.part_number}
              <FaCopy className="copy-icon" />
            </div>
          </div>
          
          {/* 상품 설명 및 상세 정보 */}
          <div className="description-section">
            <div className="description">
              {rowData.description}
            </div>
            <div className="detail-info">
              <span className="cas-info">CAS: {rowData.cas_number || '-'}</span>
              <span className="stock-info">재고: {rowData.quantity > 0 ? rowData.quantity : '-'}</span>
              <span className="unit-info">단위: {rowData.unit}</span>
            </div>
          </div>
          
          {/* 상태 */}
          <div className="status-section">
            <span className={cx('status-badge', rowData.status)}>
              {status[rowData.status]}
            </span>
          </div>
          
          {/* 가격 정보 */}
          <div className="price-info">
            {/* 1줄: 기존 가격 (할인이 있을 때만 취소선) */}
            <div className="original-price-line">
              <span 
                className={cx('original-price', {
                  'strikethrough': !!(rowData.original_price - rowData.price)
                })}
              >
                {numberWithCommas(rowData.original_price)}원
              </span>
            </div>
            
            {/* 2줄: 할인된 가격 (할인이 있을 때만 표시) */}
            {!!(rowData.original_price - rowData.price) && (
              <div className="discounted-price-line">
                <span className="discount-label">할인가</span>
                <span className="discounted-price">
                  {numberWithCommas(rowData.price)}원
                </span>
              </div>
            )}
            
            {/* 3줄: VAT 포함 가격 */}
            <div className="vat-price-line">
              <span className="vat-label">(VAT포함)</span>
              <span className="vat-price">
                {numberWithCommas(rowData.price_vat)}원
              </span>
            </div>
          </div>
          
          {/* 수량 조절 */}
          <div className="quantity-control">
            <button 
              type="button" 
              className="quantity-btn minus"
              onClick={() => decreaseQuantity(i)}
            >
              <AiOutlineMinus />
            </button>
            <input
              className="quantity"
              onChange={onChange(i)}
              name="quantity"
              placeholder="수량"
              value={quantity[i]}
              maxLength="20"
              required
              type="text"
              pattern="[1-9]*"
            />
            <button 
              type="button" 
              className="quantity-btn plus"
              onClick={() => {
                if (quantity[i] < 1000) {
                  increaseQuantity(i);
                }
              }}
            >
              <AiOutlinePlus />
            </button>
          </div>
          
          {/* 액션 버튼 */}
          <div className="action-buttons">
            {rowData.price === '0' ? (
              <button
                className="action-btn inquiry-btn"
                onClick={() => onChannel(rowData, '상품 옵션 정보 - (데스크탑)')}
                title="문의하기"
              >
                <MdChat />
              </button>
            ) : (
              <React.Fragment>
                <button
                  className="action-btn inquiry-btn"
                  onClick={() => onChannel(rowData, '상품 옵션 정보 - (데스크탑)')}
                  title="문의하기"
                >
                  <MdChat />
                </button>
                <button
                  className="action-btn cart-btn"
                  onClick={() => addCart(rowData.id, rowData.id, i)}
                  title="장바구니 담기"
                >
                  <MdShoppingCart />
                </button>
                <button
                  className="action-btn wish-btn"
                  onClick={() => addWish(rowData.id, rowData.id, i)}
                  title="찜하기"
                >
                  <FaHeart />
                </button>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="Table">
      {/* PC 카드 그리드 레이아웃 */}
      <div className="desktop-cards-grid">
        {rows.map((row, i) => {
          prepareRow(row);
          return renderDesktopCard(row, i);
        })}
      </div>
      
      {/* 데스크톱 테이블 (숨김) */}
      <table {...getTableProps()} className="table" style={{ display: 'none' }}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                // Add the sorting props to control sorting. For this example
                // we can add them into the header props
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {/* Add a sort direction indicator */}
                  <span>{column.sorted ? (column.sortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row, i) => {
            return (
              prepareRow(row) || (
                <tr {...row.getRowProps()} key={row.id || i}>
                  {row.cells.map(cell => {
                    if (cell.column.id === 'part_number') {
                      return (
                        <td 
                          key={cell.column.id} 
                          className={cx('part-number-td', { mark: part_number === cell.value })}
                          onClick={() => {
                            if (cell.value) {
                              navigator.clipboard.writeText(cell.value)
                                .then(() => {
                                  // 복사 성공 시 시각적 피드백 제공
                                  // 현재 클릭된 요소를 직접 참조 (event.currentTarget 대신)
                                  const clickedElement = document.activeElement;
                                  const td = clickedElement.closest('td.part-number-td') || clickedElement;
                                  
                                  // 클래스 추가로 시각적 피드백
                                  td.classList.add('copied');
                                  setTimeout(() => {
                                    td.classList.remove('copied');
                                  }, 1000);
                                  
                                  // 토스트 메시지 표시
                                  BaseActions.showToast({
                                    type: 'success',
                                    message: `카탈로그 번호 "${cell.value}"가 클립보드에 복사되었습니다.`,
                                  });
                                })
                                .catch(err => {
                                  console.error('클립보드 복사 실패:', err);
                                  BaseActions.showToast({
                                    type: 'error',
                                    message: '클립보드 복사에 실패했습니다.',
                                  });
                                });
                            }
                          }}
                          title="클릭하여 복사하기"
                        >
                          {cell.value}
                          <FaCopy className="copy-icon" />
                        </td>
                      );
                    }
                    if (cell.column.id === 'cas_number') {
                      return <td key={cell.column.id} className="cas-number-td" {...cell.getCellProps()}>{cell.value || '-'}</td>;
                    }
                    if (cell.column.id === 'description') {
                      return <td key={cell.column.id} className="description-td" {...cell.getCellProps()}>{cell.value}</td>;
                    }
                    if (cell.column.id === 'status') {
                      return (
                        <td key={cell.column.id} className="status-td">
                          <span className={cx('status', cell.value)}>{status[cell.value]}</span>
                        </td>
                      );
                    }
                    if (cell.column.id === 'unit') {
                      return <td key={cell.column.id} className="unit-td">{cell.value}</td>;
                    }
                    if (cell.column.id === 'quantity') {
                      return <td key={cell.column.id} className="quantity-td">{cell.value}</td>;
                    }
                    if (cell.column.id === 'original_price') {
                      return <td key={cell.column.id} className="original-price-td">{numberWithCommas(cell.value)}원</td>;
                    }
                    if (cell.column.id === 'price') {
                      return (
                        <td key={cell.column.id} className="price-td">
                          {!(cell.row.values.original_price - cell.row.values.price) && (
                            <span className="cart-items-sale-price">
                              -
                            </span>
                          )}
                          {!!(cell.row.values.original_price - cell.row.values.price) && (
                            <span className="cart-items-sale-price">
                              {numberWithCommas(cell.row.values.price)}원
                            </span>
                          )}
                          {!!(cell.row.values.original_price - cell.row.values.price) && (
                            <span className="cart-items-compare-price">
                              {numberWithCommas(cell.row.values.original_price)}원
                            </span>
                          )}
                          {!!(cell.row.values.original_price - cell.row.values.price) && (
                            <span className="cart-items-discounted">
                              (-{numberWithCommas(cell.row.values.original_price - cell.row.values.price)}원)
                            </span>
                          )}
                        </td>
                      );
                    }
                    if (cell.column.id === 'price_vat') {
                      return <td key={cell.column.id} className="price-vat-td">{numberWithCommas(cell.value)}원</td>;
                    }
                    if (cell.column.id === 'id') {
                      return (
                        <td className="id-td" key={cell.value}>
                          <div className="quantity-control">
                            <button 
                              type="button" 
                              className="quantity-btn minus"
                              onClick={() => decreaseQuantity(i)}
                            >
                              -
                            </button>
                            <input
                              className="quantity"
                              onChange={onChange(i)}
                              name="quantity"
                              placeholder="수량"
                              value={quantity[i]}
                              maxLength="20"
                              required
                              type="text"
                              pattern="[1-9]*"
                            />

                            <button 
                              type="button" 
                              className="quantity-btn plus"
                              onClick={() => {
                                if (quantity[i] < 1000) {
                                  increaseQuantity(i);
                                }
                              }}
                            >
                              +
                            </button>
                          </div>
                          {cell.row.values.price === '0' ? (
                            <Button
                              theme="outline"
                              color="green"
                              onClick={onChannel}
                            >
                              <MdChat className="chat-icon" />
                            </Button>
                          ) : (
                            <Button
                              theme="outline"
                              color="blue"
                              onClick={() => addCart(cell.value, cell.row.original.id, i)}
                            >
                              <MdShoppingCart className="cart-icon" />
                            </Button>
                          )}
                          <Button
                            theme="outline"
                            color="red"
                            onClick={() => addWish(cell.value, cell.row.original.id, i)}
                          >
                            <FaHeart className="wish-icon" />
                          </Button>
                        </td>
                      );
                    }
                    return <td key={cell.column.id} {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                  })}
                </tr>
              )
            );
          })}
        </tbody>
      </table>
      
      {/* 모바일 카드 레이아웃 */}
      <div className="mobile-cards">
        {rows.map((row, i) => {
          prepareRow(row);
          return renderMobileCard(row, i);
        })}
      </div>
      

    </div>
  );
}

type Props = {
  productPrice: string,
  refreshProductData: () => Promise<boolean>,
} & ContextRouter;

const ProductPrice = ({
  user,
  logged,
  currentUsername,
  onLogin,
  productId,
  productPrice,
  history,
  onChannel,
  refreshProductData,
  supplier,
}: Props) => {
  const serverData = productPrice;
  const columns = React.useMemo(
    () => [
      {
        Header: '상품 옵션 정보',
        columns: [
          {
            Header: '카탈로그 번호',
            accessor: 'part_number',
            className: 'part-number-column',
          },
          {
            Header: 'CAS 번호',
            accessor: 'cas_number',
            className: 'cas-number-column',
          },
          {
            Header: '설명',
            accessor: 'description',
            className: 'description-column',
          },
          {
            Header: '상태',
            accessor: 'status',
            className: 'status-column',
          },
          {
            Header: '재고',
            accessor: 'quantity',
            className: 'quantity-column',
          },
          {
            Header: '단위',
            accessor: 'unit',
            className: 'unit-column',
          },
          {
            Header: '판매가',
            accessor: 'original_price',
            className: 'original-price-column',
          },
          {
            Header: '할인가',
            accessor: 'price',
            className: 'price-column',
          },
          {
            Header: '가격(VAT포함)',
            accessor: 'price_vat',
            className: 'price-vat-column',
          },
          {
            Header: '수량 / 장바구니 / 찜',
            accessor: 'id',
            className: 'actions-column',
          },
        ],
      },
    ],
    [],
  );

  // We'll start our table without any data
  const [data, setData] = React.useState(serverData);
  const [loading, setLoading] = React.useState(false);

  const [totalCount, setTotalCount] = React.useState(serverData.length);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [countdown, setCountdown] = React.useState(null);
  const [autoRefreshCount, setAutoRefreshCount] = React.useState(0);
  const startAutoRefreshRef = React.useRef(null);
  const isMountedRef = React.useRef(true); // 컴포넌트 마운트 상태 추적용 ref
  
  // 제품이 Merck Sigma 공급업체인지 확인
  const isMerckSigma = React.useMemo(() => {
    return supplier === 'Merck Sigma' || supplier === 'Thermo Fisher Scientific';
  }, [supplier]);

  // 수량 배열 초기화 - React.useMemo를 사용하여 렌더링 중 상태 업데이트 방지
  const quantityArray = React.useMemo(() => {
    const arr = [];
    serverData.forEach(() => arr.push(1));
    return arr;
  }, [serverData]);
  
  const [quantity, setQuantity] = React.useState(quantityArray);

  // 자동 새로고침 시작 함수
  const startAutoRefresh = React.useCallback(() => {
    // 이미 2회 이상 실행했으면 중단
    if (autoRefreshCount >= 2 || !isMountedRef.current) {
      if (isMountedRef.current) setCountdown(null);
      return undefined;
    }
    
    if (isMountedRef.current) setCountdown(10); // 10초 카운트다운 시작
    
    const timer = setInterval(() => {
      if (!isMountedRef.current) {
        clearInterval(timer);
        return;
      }
      
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(timer);
          // 새로고침 실행
          if (isMountedRef.current) setIsRefreshing(true);
          refreshProductData()
            .then(success => {
              if (success && isMountedRef.current) {
                // 자동 새로고침 카운트 증가
                setAutoRefreshCount(prev => {
                  const newCount = prev + 1;
                  // 2번 미만 실행했으면 다음 자동 새로고침 시작
                  if (newCount < 2 && isMountedRef.current) {
                    setTimeout(() => {
                      if (startAutoRefreshRef.current && isMountedRef.current) {
                        startAutoRefreshRef.current();
                      }
                    }, 1000);
                  }
                  return newCount;
                });
              }
              return undefined;
            })
            .catch(error => {
              if (isMountedRef.current) {
                console.error('자동 새로고침 오류:', error);
              }
              return undefined;
            })
            .finally(() => {
              if (isMountedRef.current) setIsRefreshing(false);
            });
          return null;
        }
        return prevCount - 1;
      });
    }, 1000);
    
    const cleanup = () => {
      clearInterval(timer);
    };
    
    return cleanup;
  }, [refreshProductData, autoRefreshCount]);
  
  // 전체 상품 정보 새로고침 함수
  const handleRefreshAll = React.useCallback(async () => {
    if (isRefreshing || !isMountedRef.current) return;
    
    setIsRefreshing(true);
    try {
      // ProductViewer에서 전달받은 refreshProductData 함수 호출
      const success = await refreshProductData();
      
      if (success && isMountedRef.current) {
        // 성공 시 UI 업데이트는 ProductViewer에서 자동으로 처리됨
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('상품 정보 새로고침 오류:', error);
      }
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [refreshProductData, isRefreshing]);

  const fetchData = React.useCallback(
    () => {
      // Set the loading state
      setLoading(true);

      // We'll even set a delay to simulate a server here
      setTimeout(() => {
        // Show all data without pagination
        setData(serverData);
        setTotalCount(serverData.length);

        setLoading(false);
      }, 1000);
    },
    [serverData],
  );
  
  // startAutoRefreshRef에 startAutoRefresh 함수 할당
  React.useEffect(() => {
    startAutoRefreshRef.current = startAutoRefresh;
  }, [startAutoRefresh]);
  
  // 컴포넌트 마운트/언마운트 상태 관리
  React.useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // 컴포넌트 마운트 시 자동 새로고침 시작 (Merck Sigma 제품일 때만)
  React.useEffect(() => {
    // Merck Sigma 제품일 때만 자동 새로고침 시작
    if (isMerckSigma && isMountedRef.current) {
      const timer = setTimeout(() => {
        if (startAutoRefreshRef.current && isMountedRef.current) {
          startAutoRefreshRef.current();
        }
      }, 500);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isMerckSigma]);
  
  // 컴포넌트 언마운트 시 카운트다운 중지
  React.useEffect(() => {
    return () => {
      if (isMountedRef.current) {
        setCountdown(null);
      }
    };
  }, []);

  return (
    <div className="ProductPrice">
      {/* 새로고침 컨트롤 영역 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: '1rem', alignItems: 'center' }}>
        {/* Merck Sigma 제품일 때만 카운트다운과 새로고침 버튼 표시 */}
        {isMerckSigma && countdown !== null && (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#6c757d',
              background: '#f8f9fa',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              border: '1px solid #e9ecef'
            }}
          >
            <MdTimer style={{ fontSize: '1.125rem', color: '#007bff' }} />
            <span>{countdown}초 후 자동 새로고침 ({autoRefreshCount}/2)</span>
          </div>
        )}
        {/* Merck Sigma 제품일 때만 새로고침 버튼 표시 */}
        {isMerckSigma && (
          <Button 
            theme="outline" 
            color="blue" 
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              fontSize: '0.875rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontWeight: '600'
            }}
          >
            <MdAutorenew 
              style={{ 
                fontSize: '1.125rem',
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none' 
              }} 
            />
            {isRefreshing ? '새로고침 중...' : '가격 정보 새로고침'}
          </Button>
        )}
      </div>
      
      {/* 상품 옵션 정보 테이블 컨테이너 */}
      <div className="product-options-container">
        <div className="product-options-header">
          <h3>상품 옵션 정보</h3>
          <p>다양한 옵션의 상품 정보와 가격을 확인하세요</p>
        </div>
        
        <Styles>
          <Table
            className="Table"
            columns={columns}
            data={data}
            user={user}
            logged={logged}
            currentUsername={currentUsername}
            onLogin={onLogin}
            productId={productId}
            fetchData={fetchData}
            loading={loading}

            serverData={serverData}
            setData={setData}
            totalCount={totalCount}
            history={history}
            quantity={quantity}
            setQuantity={setQuantity}
            onChannel={onChannel}
          />
        </Styles>
      </div>
    </div>
  );
};

export default withRouter(ProductPrice);



// WEBPACK FOOTER //
// ./src/components/product/ProductPrice/ProductPrice.js